import { ThemeEvents, VariantUpdateEvent } from './events.js';

/**
 * Custom element for the finance banner section.
 *
 * Listens for variant update events and recalculates the displayed monthly
 * finance price whenever the selected variant changes on a product page.
 *
 * Expected data attributes (set by the Liquid section when a product is bound):
 * - `data-product-id`   — Shopify product ID used to filter variant update events
 * - `data-term-months`  — Finance term length in months (default: 12)
 * - `data-money-format` — Shopify `shop.money_format` template string for formatting
 * - `data-currency`     — ISO 4217 currency code, e.g. `"GBP"` (fallback for Intl)
 * - `data-locale`       — BCP 47 locale code, e.g. `"en-GB"` (fallback for Intl)
 *
 * @element finance-banner-component
 * @extends HTMLElement
 */
class FinanceBannerComponent extends HTMLElement {
	#controller = new AbortController();

	/**
	 * Registers a listener for variant update events when the element is
	 * connected to the DOM. Does nothing if `data-product-id` is not set.
	 */
	connectedCallback() {
		this.#controller = new AbortController();
		const productId = this.dataset.productId;
		if (!productId) return;

		const { signal } = this.#controller;

		document.addEventListener(
			ThemeEvents.variantUpdate,
			/** @param {VariantUpdateEvent} event */
			(event) => {
				if (String(event.detail.data?.productId) !== String(productId)) return;

				const variant = event.detail.resource;
				if (!variant || !variant.price) return;

				this.#updatePrice(variant.price);
			},
			{ signal }
		);
	}

	/**
	 * Removes the variant update listener when the element leaves the DOM.
	 */
	disconnectedCallback() {
		this.#controller.abort();
	}

	/**
	 * Recalculates the monthly finance price from the variant's full price and
	 * writes the formatted value to the `[data-finance-price]` element.
	 *
	 * @param {number} priceInCents - Variant price in the shop's minor currency unit (e.g. pence).
	 */
	#updatePrice(priceInCents) {
		const termMonths = parseInt(this.dataset.termMonths ?? '', 10) || 12;
		const monthlyPriceCents = Math.floor(priceInCents / termMonths);
		const monthlyPriceDecimal = monthlyPriceCents / 100;

		const priceEl = this.querySelector('[data-finance-price]');
		if (!priceEl) return;

		const moneyFormat = this.dataset.moneyFormat;
		if (moneyFormat) {
			priceEl.textContent = this.#formatWithTemplate(moneyFormat, monthlyPriceDecimal);
		} else {
			priceEl.textContent = this.#formatWithIntl(monthlyPriceDecimal);
		}
	}

	/**
	 * Formats a decimal amount using a Shopify money format template string.
	 *
	 * Supports the following Shopify format tokens:
	 * - `{{ amount }}`                              — two decimal places, e.g. `"9.99"`
	 * - `{{ amount_no_decimals }}`                  — rounded integer, e.g. `"10"`
	 * - `{{ amount_with_comma_separator }}`         — two decimal places, comma as decimal separator, e.g. `"9,99"`
	 * - `{{ amount_no_decimals_with_comma_separator }}` — rounded integer, comma as thousands separator, e.g. `"1,000"`
	 *
	 * @param {string} format - Shopify `shop.money_format` template string.
	 * @param {number} amount - Decimal amount to format, e.g. `9.99`.
	 * @returns {string} The formatted price string.
	 */
	#formatWithTemplate(format, amount) {
		return format
			.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount.toFixed(2).replace('.', ','))
			.replace(
				/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g,
				Math.round(amount)
					.toString()
					.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
			)
			.replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount).toString())
			.replace(/\{\{\s*amount\s*\}\}/g, amount.toFixed(2));
	}

	/**
	 * Formats a decimal amount as a localised currency string using the
	 * Intl.NumberFormat API. Used as a fallback when no money format template
	 * is available.
	 *
	 * @param {number} amount - Decimal amount to format, e.g. `9.99`.
	 * @returns {string} The formatted price string, e.g. `"£9.99"`.
	 */
	#formatWithIntl(amount) {
		const currency = this.dataset.currency || 'GBP';
		const locale = this.dataset.locale || 'en-GB';
		return new Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
		}).format(amount);
	}
}

if (!customElements.get('finance-banner-component')) {
	customElements.define('finance-banner-component', FinanceBannerComponent);
}
